import {
    createComponentById,
    NO_PATTERN_BUSINESS_LOGIC,
    Price,
} from '@vfde-brix/ws10/core';
import { StateProps } from 'Container/App/interface';
import {
    createOfferSummaryCard,
    IOfferSummaryCardAdditionalPrice,
    IOfferSummaryCardPromoPrice,
    OfferSummaryCard,
} from '@vfde-brix/ws10/offer-summary-card';
import {
    composeLegalText,
    createTooltipId,
    getHeadline,
    getSubline,
} from 'Container/App/helpers/offerSummaryCardHelper';
import { ADDITIONAL_PAGE_OPTIONS } from '@vfde-sails/constants';
import { IButtonProperties } from '@vfde-brix/ws10/button';
import { IHighlightBadgeProperties } from '@vfde-brix/ws10/highlight-badge';
import { getShippingFee } from '@vfde-sails/page-options';
import { replacePlaceholders } from '@vfde-sails/utils';
import {
    goToBasket,
    selectCustomerConfig,
    selectSkipConnectMore,
} from 'Container/App/slice';
import { startAppListening } from 'Container/App/listener';
import {
    createStructuredAppSelector,
    selectCurrentOfferData,
    selectDataVolume,
    selectDeviceData,
    selectEndPriceToPay,
    selectPriceToPay,
    selectStairway,
    selectStrikePrice,
    selectTariffPrice,
} from 'Container/App/selectors';
import { useDispatch } from '../store';
import { fetchOverlay } from '@vfde-sails/overlay';
import {
    GIGAKOMBI_OVERLAY_CONTAINER_ID,
    OFFER_SUMMARY_CARD_CONTAINER,
    STICKY_PRICE_BAR_CONTAINER,
} from 'Container/App/constants';
import {
    isTariffWithHardwareOffer,
    PriceType,
} from '@vfde-sails/glados-v2';
import { createStickyPriceBar } from '@vfde-brix/ws10/sticky-price-bar';

const mapStateToOfferPriceProps = createStructuredAppSelector({
    activeOffer: selectCurrentOfferData,
    device: selectDeviceData,
    customerConfig: selectCustomerConfig,
    dataVolume: selectDataVolume,
    strikePrice: selectStrikePrice,
    priceToPay: selectPriceToPay,
    endPriceToPay: selectEndPriceToPay,
    hasStairway: selectStairway,
    tariffPrice: selectTariffPrice,
    skipConnectMore: selectSkipConnectMore,
});

/**
 * Mount offer price
 */
export const mountOfferSummaryCard = (): OfferSummaryCard | null => {
    const offerSummaryCard = createComponentById(createOfferSummaryCard, OFFER_SUMMARY_CARD_CONTAINER, NO_PATTERN_BUSINESS_LOGIC);
    const stickyPriceBar = createComponentById(createStickyPriceBar, STICKY_PRICE_BAR_CONTAINER, {
        stickyPriceBarItems: [],
        business: NO_PATTERN_BUSINESS_LOGIC,
    });

    offerSummaryCard && stickyPriceBar && offerSummaryCard.connectStickyPriceBar(stickyPriceBar);

    startAppListening({
        predicate: (_action, currentState, previousState) =>
            selectCurrentOfferData(currentState) !== selectCurrentOfferData(previousState)
            || selectSkipConnectMore(currentState) !== selectSkipConnectMore(previousState),
        effect: (_action, api) => {
            const offerPriceProps = mapStateToOfferPriceProps(api.getState());

            offerSummaryCard && updateOfferSummaryCard(offerSummaryCard, offerPriceProps);
        },
    });

    return offerSummaryCard;
};

/**
 * Update offer price
 */
const updateOfferSummaryCard = (offerSummaryCard: OfferSummaryCard, state: ReturnType<typeof mapStateToOfferPriceProps>) => {
    const originalProperties = offerSummaryCard.getOriginalProperties();

    const { activeOffer } = state;

    if (!activeOffer) {
        return;
    }

    const legalText = composeLegalText(state);
    const allLegalText = [legalText].filter(Boolean) as string[];
    const subline = getSubline(state);
    const highlightBadge = getHighlightBadge(state);

    const promoPrices = Object.values(
        getTariffAndDevicePrices(
            state,
            originalProperties.containerPromoPrices![1],
            originalProperties.containerPromoPrices![0],
        ),
    ).filter(Boolean) as IOfferSummaryCardPromoPrice[];
    const additionalPrices = getAdditionalPrices(state);

    offerSummaryCard
        .updateSubline(subline ? [subline] : originalProperties.txtSubline)
        .updateLegalText(allLegalText)
        .updateHeadline({
            stdContent: getHeadline(state),
        })
        .updatePrices(
            promoPrices,
            Object.values(additionalPrices).filter(Boolean) as IOfferSummaryCardAdditionalPrice[],
        )
        .updateHighlightBadge(highlightBadge || originalProperties.containerHighlightBadge)
        .updateButtons(getButtons(state, originalProperties.containerButtons || []))
    ;

    offerSummaryCard.stickyPriceBar?.update({
        stickyPriceBarItems: [
            ...OfferSummaryCard.convertPromoPriceItemsToStickyPriceBarItems(promoPrices),
            ...OfferSummaryCard.convertAdditionalPriceItemsToStickyPriceBarItems(additionalPrices.stairway),
        ],
    });

    offerSummaryCard.toggleHighlightBadgeContainer(!!highlightBadge);
};

const getTariffAndDevicePrices = (
    state: Pick<StateProps, 'activeOffer' | 'priceToPay' | 'strikePrice' | 'device'>,
    originalTariffPrice: IOfferSummaryCardPromoPrice,
    originalDevicePrice: IOfferSummaryCardPromoPrice,
): {
    [PriceType.Onetime]: IOfferSummaryCardPromoPrice,
    [PriceType.Monthly]: IOfferSummaryCardPromoPrice | null,
} => {
    const prices = {} as ReturnType<typeof getTariffAndDevicePrices>;

    const { priceToPay, strikePrice, activeOffer, device } = state;

    if (device && isTariffWithHardwareOffer(activeOffer)) {
        prices[PriceType.Onetime] = {
            ...originalDevicePrice,
            containerPromoPrice: {
                ...originalDevicePrice.containerPromoPrice,
                optPriceConditionBefore: undefined,
                strikePrice: undefined,
                price: Price.fromNumber(activeOffer.priceHardwareOneTime.gross),
            },
        };
    }

    prices[PriceType.Monthly] = {
        ...originalTariffPrice,
        containerPromoPrice: {
            ...originalTariffPrice.containerPromoPrice,
            optPriceConditionBefore: undefined,
            strikePrice: strikePrice ? Price.fromNumber(strikePrice.gross) : undefined,
            ...priceToPay && { price: Price.fromNumber(priceToPay.gross) },
        },
    };

    return prices;
};

const getAdditionalPrices = (
    state: Pick<StateProps, 'activeOffer' | 'hasStairway' | 'device' | 'tariffPrice' | 'endPriceToPay'>,
): {
    stairway: IOfferSummaryCardAdditionalPrice | null,
    connectionFee: IOfferSummaryCardAdditionalPrice | null,
    shippingFee: IOfferSummaryCardAdditionalPrice | null,
} => {
    const prices: ReturnType<typeof getAdditionalPrices> = {
        stairway: null,
        connectionFee: null,
        shippingFee: null,
    };

    const { hasStairway, device, tariffPrice, endPriceToPay, activeOffer } = state;

    const shippingFee = device ? getShippingFee() : undefined;

    if (hasStairway && endPriceToPay) {
        prices.stairway = {
            txtLabel: replacePlaceholders(window[ADDITIONAL_PAGE_OPTIONS].offerSummaryCard.stairwayTextPattern, {
                month: `${tariffPrice!.recurrenceEnd + 1}`,
            }),
            price: Price.fromNumber(endPriceToPay.gross),
        };
    }

    if (activeOffer?.priceConnectionFee) {
        prices.connectionFee = {
            txtLabel: window[ADDITIONAL_PAGE_OPTIONS].offerSummaryCard.connectionFeeLabel,
            price: Price.fromNumber(activeOffer.priceConnectionFee.gross),
        };
    }

    if (shippingFee) {
        prices.shippingFee = {
            txtLabel: shippingFee.label,
            price: Price.fromNumber(shippingFee.gross),
        };
    }

    return prices;
};

const getHighlightBadge = (state: Pick<StateProps, 'activeOffer' | 'customerConfig'>): Partial<IHighlightBadgeProperties> | null => {
    const highlightBadgeForOffer = window[ADDITIONAL_PAGE_OPTIONS].highlightBadges?.[state.activeOffer!.tariffId];

    const {
        text,
        tooltip,
        icon,
        color,
    } = highlightBadgeForOffer || {};

    if (!text) {
        return null;
    }

    return {
        txtContent: text,
        ...icon && { stdIcon: icon },
        ...color && { optColor: color },
        containerTooltip: tooltip ? {
            stdHeadline: tooltip.headline!,
            txtContent: `<p>${tooltip.text!}</p>`,
            stdTooltipId: createTooltipId(state.activeOffer, state.customerConfig.hasGigakombi),
            business: NO_PATTERN_BUSINESS_LOGIC,
        } : undefined,
    };
};

const getButtons = (
    state: Pick<StateProps, 'activeOffer' | 'device' | 'skipConnectMore' | 'customerConfig'>,
    originalButtons: IButtonProperties[],
): IButtonProperties[] => {
    const dispatch = useDispatch();

    const buttons: IButtonProperties[] = [];

    const isHardwareSelected = !!state.device;
    const { extrasButtonText, basketButtonText } = window[ADDITIONAL_PAGE_OPTIONS].offerSummaryCard;

    const primaryButton: IButtonProperties = {
        ...originalButtons[0],
        stdContent: state.skipConnectMore ? basketButtonText : extrasButtonText,
        optDisabled: false,
        business: {
            onClick: () => state.skipConnectMore
                ? dispatch(goToBasket())
                : window.location.assign(window[ADDITIONAL_PAGE_OPTIONS].offerSummaryCard.extrasButtonUrl),
        },
    };

    if (state.customerConfig.hasGigakombi) {
        primaryButton.stdContent = basketButtonText;

        primaryButton.business.onClick = () => {
            const url = window[ADDITIONAL_PAGE_OPTIONS].offerSummaryCard.gigakombiOverlay;
            dispatch(fetchOverlay({
                url,
                context: {
                    customerConfig: state.customerConfig,
                    trackingData: {
                        trackingName: GIGAKOMBI_OVERLAY_CONTAINER_ID,
                    },
                },
            }));
        };
    }

    buttons.push(primaryButton);

    if (!isHardwareSelected) {
        buttons.push({
            ...originalButtons[1],
        });
    }

    return buttons;
};
